"use client"

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { API_BASE_URL, ATHENA_SESSION_TOKEN } from '../../lib/config'
import SessionTimeout from '../../client/components/SessionTimeout'
import AccessDeniedOverlay from '../../client/components/AccessDeniedOverlay'

export interface SessionUser {
  id: string | number
  username: string
  email: string
  displayName?: string
}

interface AuthContextType {
  user: SessionUser | null
  loading: boolean
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>
  isSignedIn: boolean
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/** Decode JWT payload without verification (payload only). Returns null if invalid or expired. */
function decodeSessionToken(token: string): { sub: string; username?: string; email?: string; exp?: number } | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    if (payload.exp != null && payload.exp < Date.now() / 1000) return null
    return payload
  } catch {
    return null
  }
}

function readUserFromStorage(): SessionUser | null {
  if (typeof localStorage === 'undefined') return null
  const token = localStorage.getItem(ATHENA_SESSION_TOKEN)
  if (!token) return null
  const payload = decodeSessionToken(token)
  if (!payload || !payload.sub) return null
  return {
    id: payload.sub,
    username: payload.username ?? payload.sub,
    email: payload.email ?? '',
    displayName: payload.username ?? payload.email ?? payload.sub,
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [sessionEnded, setSessionEnded] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)

  const refreshSession = useCallback((): Promise<void> => {
    const nextUser = readUserFromStorage()
    setUser(nextUser)
    if (!nextUser) setIsAdmin(false)
    setLoading(false)
    return Promise.resolve()
  }, [])

  useEffect(() => {
    refreshSession()
  }, [refreshSession])

  useEffect(() => {
    if (!user) return
    let cancelled = false
    fetch(`${API_BASE_URL}auth/me`, { credentials: 'include' })
      .then(async (res) => {
        if (cancelled) return null
        if (res.ok) {
          const data = await res.json()
          return { ok: true as const, data }
        }
        if (res.status === 401) {
          localStorage.removeItem(ATHENA_SESSION_TOKEN)
          setSessionEnded(true)
          setUser(null)
          setIsAdmin(false)
        } else if (res.status === 403) {
          const body = await res.json().catch(() => ({}))
          if (body?.error === 'access_denied') setAccessDenied(true)
        }
        return null
      })
      .then((result) => {
        if (cancelled || !result?.ok) return
        setUser({
          id: result.data.id,
          username: result.data.username,
          email: result.data.email || '',
          displayName: result.data.display_name ?? result.data.displayName ?? result.data.username,
        })
        setAccessDenied(false)
        setSessionEnded(false)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
    return () => { cancelled = true }
  }, [user?.id])

  useEffect(() => {
    if (!user?.email) {
      setIsAdmin(false)
      return
    }
    let cancelled = false
    fetch(`${API_BASE_URL}admin/check-status`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'x-user-email': user.email },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setIsAdmin(!!data.is_admin)
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false)
      })
    return () => {
      cancelled = true
    }
  }, [user?.email])

  const navigate = useNavigate()
  const signOutUser = async () => {
    try {
      setLoading(true)
      await fetch(`${API_BASE_URL}logout`, { method: 'POST', credentials: 'include' })
      setUser(null)
      localStorage.removeItem(ATHENA_SESSION_TOKEN)
      localStorage.removeItem('backend-authed')
      localStorage.removeItem('initial-chat-created')
      localStorage.removeItem('welcome-seen')
      navigate('/session-timeout', { replace: true })
    } catch (error) {
      console.error('Error signing out:', error)
      setUser(null)
      localStorage.removeItem(ATHENA_SESSION_TOKEN)
      navigate('/session-timeout', { replace: true })
    } finally {
      setLoading(false)
    }
  }

  const isSignedIn = !!user

  const value = {
    user,
    loading,
    signOut: signOutUser,
    refreshSession,
    isSignedIn,
    isAdmin,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeout
        visible={sessionEnded && !user}
        onDismiss={() => setSessionEnded(false)}
      />
      <AccessDeniedOverlay
        visible={accessDenied}
        onDismiss={() => setAccessDenied(false)}
      />
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function SignedIn({ children }: { children: React.ReactNode }) {
  const { isSignedIn, loading } = useAuth()
  if (loading) return null
  return isSignedIn ? <>{children}</> : null
}

export function SignedOut({ children }: { children: React.ReactNode }) {
  const { isSignedIn, loading } = useAuth()
  if (loading) return null
  return !isSignedIn ? <>{children}</> : null
}

interface AdminStatus {
  is_admin: boolean
  user_email: string | null
  user_role: string | null
}

export function UserButton({
  showName = true,
  size = 'default',
  disableHover = false,
  dropdownPosition = 'below',
}: {
  showName?: boolean
  size?: 'default' | 'mini'
  disableHover?: boolean
  dropdownPosition?: 'above' | 'below'
}) {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [adminStatus, setAdminStatus] = useState<AdminStatus | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.email) return
      try {
        const response = await fetch(`${API_BASE_URL}admin/check-status`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'x-user-email': user.email },
        })
        if (response.ok) {
          const status = await response.json()
          setAdminStatus(status)
        }
      } catch (error) {
        console.error('Error checking admin status:', error)
      }
    }
    checkAdminStatus()
  }, [user])

  const isAdmin = adminStatus?.is_admin || false

  if (!user) return null

  const displayName = user.username || user.email?.split('@')[0] || 'User'
  const initial = (displayName.charAt(0) || 'U').toUpperCase()

  if (size === 'mini') {
    return (
      <div className="relative group">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-7 h-7 p-0 rounded-lg hover:bg-light-accent/50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer ${isOpen ? 'bg-light-accent/50 dark:bg-slate-800/50' : ''}`}
          title={displayName}
        >
          <span className="w-full h-full rounded-lg bg-brand-blue text-white text-xs font-medium inline-flex items-center justify-center">
            {initial}
          </span>
        </button>
        {isOpen && (
          <div
            className={`absolute w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 z-[9999] ${
              dropdownPosition === 'above' ? 'bottom-full right-0 mb-2' : 'top-full right-0 mt-2'
            }`}
          >
            <div className="p-3 border-b border-gray-200 dark:border-slate-700">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={user.email}>
                {user.email}
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={() => {
                  setIsOpen(false)
                  navigate('/admin')
                }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
              >
                <Shield className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                Admin Panel
              </button>
            )}
          </div>
        )}
        {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />}
      </div>
    )
  }

  return (
    <div className="relative group">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer ${!disableHover ? 'hover:bg-light-accent/50 dark:hover:bg-slate-800/50' : ''} ${isOpen ? 'bg-light-accent/50 dark:bg-slate-800/50' : ''}`}
      >
        <span className="w-7 h-7 rounded-lg bg-brand-blue text-white text-sm font-medium flex items-center justify-center flex-shrink-0">
          {initial}
        </span>
        {showName && (
          <span className="text-sm font-medium text-light-text dark:text-white">{displayName}</span>
        )}
      </button>
      {isOpen && (
        <div
          className={`absolute w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 z-[9999] ${
            dropdownPosition === 'above' ? 'bottom-full right-0 mb-2' : 'top-full right-0 mt-2'
          }`}
        >
          <div className="p-3 border-b border-gray-200 dark:border-slate-700">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayName}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={user.email}>
              {user.email}
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/admin')
              }}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
            >
              <Shield className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              Admin Panel
            </button>
          )}
        </div>
      )}
      {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />}
    </div>
  )
}

export function SignInButton({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigate('/login')}
      className="inline-flex items-center justify-center"
    >
      {children}
    </button>
  )
}
